import { useEffect, useRef } from 'react'

import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import ErrorBanner from '@/buyer-portal/components/ErrorBanner'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import {
  PARCEL_NO_RATES_MESSAGE_KEYS,
  type ParcelNoRatesErrorCode,
} from './types'

// Spacing above/below is owned by the enclosing ParcelRateSection (heading +
// section margin); the banner itself stays flush.
const Wrapper = styled.div``

export type ParcelNoRatesBannerProps = {
  errorCode: ParcelNoRatesErrorCode
}

/**
 * FP-1906: blocking state shown in place of the parcel rate list when the
 * backend returns no usable rate. Wrapped in `role="alert"` so screen readers
 * announce it on appearance, and fires `errorViewed` once per occurrence.
 */
export const ParcelNoRatesBanner = ({
  errorCode,
}: ParcelNoRatesBannerProps) => {
  const { errorViewed } = useAnalytics()
  const trackedCodeRef = useRef<ParcelNoRatesErrorCode | null>(null)

  useEffect(() => {
    if (trackedCodeRef.current === errorCode) return
    trackedCodeRef.current = errorCode
    errorViewed({
      screenName: 'parcel_no_rates',
      source: AnalyticsSource.HOME_PORTAL,
      intention: 'buyer',
      entityType: AnalyticsEntityType.BUYER,
      errorType: 'technical',
      errorMessage: errorCode,
    })
  }, [errorCode, errorViewed])

  return (
    <Wrapper role="alert" data-testid="parcel-no-rates-banner">
      <ErrorBanner>
        <FormattedMessage id={PARCEL_NO_RATES_MESSAGE_KEYS[errorCode]} />
      </ErrorBanner>
    </Wrapper>
  )
}
